import { createContext, useCallback, useState, useEffect } from "react";

const MultipleAssessmentsContext = createContext();

export function MultipleAssessmentsContextProvider(props) {
  const [deploymentInfo, setDeploymentInfo] = useState(null);
  const [isLoadingDeploymentInfo, setIsLoadingDeploymentInfo] = useState(false);
  const [deployments, setDeployments] = useState([]);
  const [currentAssessmentId, setCurrentAssessmentId] = useState(null);
  const [previousAssessmentId, setPreviousAssessmentId] = useState(null);
  const [selectedDeploymentIds, setSelectedDeploymentIds] = useState([]);
  const [is404Error, set404Error] = useState(false);
  const [parishInsights, setParishInsights] = useState(null);
  const [isLoadingParishInsights, setIsLoadingParishInsights] = useState(false);

  // Function to set assessment ID and detect changes
  const setAssessmentId = useCallback((newAssessmentId) => {
    if (newAssessmentId && newAssessmentId !== currentAssessmentId) {
      setPreviousAssessmentId(currentAssessmentId);
      setCurrentAssessmentId(newAssessmentId);
      set404Error(false);

      // Save to localStorage
      localStorage.setItem('currentAssessmentId', newAssessmentId);


      return true; // ID has changed
    }
    return false; // ID has not changed
  }, [currentAssessmentId]);

  // Function to get assessment ID from localStorage on page reload
  const getAssessmentIdFromStorage = useCallback(() => {
    const storedId = localStorage.getItem('currentAssessmentId');
    if (storedId && storedId !== currentAssessmentId) {
      setCurrentAssessmentId(storedId);
      return storedId;
    }
    return currentAssessmentId;
  }, [currentAssessmentId]);

  // Function to check if assessment ID has changed
  const hasAssessmentIdChanged = useCallback(() => {
    return currentAssessmentId !== previousAssessmentId && previousAssessmentId !== null;
  }, [currentAssessmentId, previousAssessmentId]);

  const createNewOrganizationAssessment = useCallback(async (hash) => {
    const base = process.env.REACT_APP_API_URI;
    const url = `${base}/v6/organisation/dr/${hash}`;
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }
    return response.json().catch(() => ({}));
  }, []);

  const fetchDeploymentInfo = useCallback(async (assessmentHashId) => {
    if (!assessmentHashId) {
      throw new Error("Assessment hash ID is required to fetch deployment info");
    }

    setIsLoadingDeploymentInfo(true);
    try {
      const base = process.env.REACT_APP_API_URI;
      const url = `${base}/v6/organisation/dr/${assessmentHashId}/deployments`;
      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      const data = await response.json();
      setDeploymentInfo(data);

      // Extract deployments from response and store them
      if (data.status === "success" && data.data && Array.isArray(data.data.deployments)) {
        setDeployments(data.data.deployments);
        set404Error(false);
      } else {
        setDeployments([]);
        set404Error(true);
      }

      return data;
    } catch (error) {
      setDeployments([]);
      set404Error(true);
      throw error;
    } finally {
      setIsLoadingDeploymentInfo(false);
    }
  }, []);

  // Auto-fetch deployment info when assessment ID changes or when deploymentInfo is null
  useEffect(() => {
    if (currentAssessmentId) {
      // Fetch if assessment ID changed OR if deploymentInfo is null
      if (hasAssessmentIdChanged() || !deploymentInfo) {
        fetchDeploymentInfo(currentAssessmentId).catch(error => {
          // Auto-fetch deployment info failed
        });
      }
    }
  }, [currentAssessmentId, hasAssessmentIdChanged, fetchDeploymentInfo, deploymentInfo]);

  const updateDeployment = useCallback(async (userAssessmentHash, deploymentId) => {
    try {
      const base = process.env.REACT_APP_API_URI;
      const url = `${base}/v4/user/assessment/${userAssessmentHash}/updateUserAssessment`;
      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deploymentId })
      });
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      return response.json();
    } catch (error) {
      throw error;
    }
  }, []);

  const fetchDeploymentInfoAndUpdate = useCallback(async (assessmentHashId, userAssessmentHash = null) => {
    if (!assessmentHashId) {
      throw new Error("Assessment hash ID is required to fetch deployment info");
    }

    setIsLoadingDeploymentInfo(true);
    try {
      // First API call to get deployment info
      const deploymentData = await fetchDeploymentInfo(assessmentHashId);


      // Check if deployments array exists and is not empty
      if (deploymentData.status === "success" &&
        deploymentData.data &&
        deploymentData.data.deployments &&
        deploymentData.data.deployments.length > 0) {

        // Get the last deployment ID from the deployments array
        const lastDeployment = deploymentData?.data?.deployments[deploymentData?.data?.deployments.length - 1];
        const deploymentId = lastDeployment.deploymentId;


        // If userAssessmentHash is provided, call update API
        if (userAssessmentHash) {
          const updateResult = await updateDeployment(userAssessmentHash, deploymentId);
          return { deploymentData, updateResult };
        }
      }

      return { deploymentData };
    } catch (error) {
      throw error;
    } finally {
      setIsLoadingDeploymentInfo(false);
    }
  }, [fetchDeploymentInfo, updateDeployment]);

  // Function to fetch deployment info if it's null
  const fetchDeploymentInfoIfNull = useCallback(async () => {
    if (!deploymentInfo && currentAssessmentId) {
      try {
        await fetchDeploymentInfo(currentAssessmentId);
      } catch (error) {
        // Failed to fetch deployment info
      }
    }
  }, [deploymentInfo, currentAssessmentId, fetchDeploymentInfo]);

  // Function to update selected deployment IDs
  const updateSelectedDeploymentIds = useCallback((deploymentIds) => {
    setSelectedDeploymentIds(deploymentIds);
  }, []);

  // Function to get the latest deploymentId from deployments array
  const getLatestDeploymentId = useCallback(() => {
    // Helper function to filter and get valid numeric deploymentIds
    const getValidDeploymentIds = (deploymentsArray) => {
      if (!deploymentsArray || deploymentsArray.length === 0) return [];
      
      return deploymentsArray
        .filter(deployment => {
          const id = deployment.deploymentId;
          // Only include numeric deploymentIds (exclude strings like "No Deployment")
          return typeof id === 'number' && !isNaN(id);
        })
        .sort((a, b) => b.deploymentId - a.deploymentId);
    };

    // Try deployments array first
    if (deployments && deployments.length > 0) {
      const validDeployments = getValidDeploymentIds(deployments);
      if (validDeployments.length > 0) {
        return validDeployments[0].deploymentId;
      }
    }
    
    // Fallback to deploymentInfo if deployments array is not available
    if (deploymentInfo?.data?.deployments && deploymentInfo.data.deployments.length > 0) {
      const validDeployments = getValidDeploymentIds(deploymentInfo.data.deployments);
      if (validDeployments.length > 0) {
        return validDeployments[0].deploymentId;
      }
    }
    
    return null;
  }, [deployments, deploymentInfo]);

  // Function to fetch parish insights for selected deployments
  const fetchParishInsights = useCallback(async (userAssessmentHash, deploymentIds) => {
    if (!userAssessmentHash || !deploymentIds || deploymentIds.length === 0) {
      throw new Error("User assessment hash and deployment IDs are required");
    }

    setIsLoadingParishInsights(true);
    try {
      const base = process.env.REACT_APP_API_URI;
      // Sort deployment IDs in ascending order before sending
      const sortedDeploymentIds = [...deploymentIds].sort((a, b) => a - b);
      const deploymentIdsParam = sortedDeploymentIds.join(',');
      const url = `${base}/v4/user/assessment/${userAssessmentHash}/parishInsights/deployments?deploymentIds=${deploymentIdsParam}`;


      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const data = await response.json();

      const enrichedDeployments = data?.deployments?.map(parishDeployment => {
        const matchingDeployment = deploymentInfo?.data?.deployments?.find(
          deployment => deployment.deploymentId === parishDeployment.deploymentId
        );
        return {
          ...parishDeployment,
          deploymentTitle: matchingDeployment?.deploymentTitle || 'Unknown'
        };
      });

      setParishInsights(enrichedDeployments);
      return {
        ...data,
        deployments: enrichedDeployments
      };
    } catch (error) {
      setParishInsights(null);
      throw error;
    } finally {
      setIsLoadingParishInsights(false);
    }
  }, [deploymentInfo]);

  const context = {
    createNewOrganizationAssessment,
    deploymentInfo,
    fetchDeploymentInfo,
    fetchDeploymentInfoIfNull,
    isLoadingDeploymentInfo,
    deployments,
    updateDeployment,
    fetchDeploymentInfoAndUpdate,
    currentAssessmentId,
    setAssessmentId,
    getAssessmentIdFromStorage,
    hasAssessmentIdChanged,
    selectedDeploymentIds,
    updateSelectedDeploymentIds,
    is404Error,
    parishInsights,
    isLoadingParishInsights,
    fetchParishInsights,
    getLatestDeploymentId,
  };

  return <MultipleAssessmentsContext.Provider value={context}>
    {props.children}
  </MultipleAssessmentsContext.Provider>
}

export default MultipleAssessmentsContext;